import { Download } from "lucide-react";
import { useEffect, useRef } from "react";

import { Button } from "@/components/ui/button";
import { track } from "@/lib/analytics";
import { logger } from "@/lib/logger";

interface DownloadReportButtonProps {
  /** Used to build the suggested PDF filename. */
  companyName: string;
}

/**
 * Turns an arbitrary company name into a safe filename stem: ASCII letters,
 * digits, and single hyphens. Falls back to "report" when nothing survives
 * (e.g. a name written entirely in a non-Latin script).
 */
function toFilenameSlug(companyName: string): string {
  const slug = companyName
    .normalize("NFKD")
    // Strip the combining marks NFKD just split off, so "Café" → "Cafe".
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    .replace(/-+$/, "");
  return slug || "report";
}

/**
 * Owner-only "Download a copy": hands the report to the browser's own print
 * engine. The report is plain HTML and CSS (no canvas, no charting library), so
 * printing gives selectable, vector text at zero bundle cost; the `@media print`
 * block in globals.css strips the page chrome.
 *
 * Chromium and Edge seed the Save-as-PDF filename from `document.title`, so we
 * swap the title for the duration of the dialog and restore it on `afterprint`.
 * Safari and Firefox ignore the title and name the file after the page; that's
 * an accepted limit of the no-dependency approach, not a bug.
 */
export function DownloadReportButton({ companyName }: DownloadReportButtonProps) {
  // Holds the pending title restore so an unmount mid-dialog can't strand the
  // swapped title or leak the listener.
  const restoreRef = useRef<(() => void) | null>(null);

  useEffect(() => () => restoreRef.current?.(), []);

  const handleClick = () => {
    track("report_shared", { method: "pdf" });

    // A second click before `afterprint` landed: undo the first swap so we
    // capture the real title rather than the previous filename.
    restoreRef.current?.();

    const previousTitle = document.title;
    const restore = () => {
      window.removeEventListener("afterprint", restore);
      document.title = previousTitle;
      restoreRef.current = null;
    };
    restoreRef.current = restore;
    window.addEventListener("afterprint", restore);

    document.title = `AI-Disruption-Report-${toFilenameSlug(companyName)}`;
    logger.debug("[report] opening print dialog as", document.title);
    window.print();
  };

  return (
    <Button variant="outline" onClick={handleClick}>
      <Download className="h-4 w-4" />
      Download a copy
    </Button>
  );
}
